import { Box, CardMedia, Theme, Typography } from "@mui/material";
import { ROUTES } from "@/utils/navigation";
import { BORDER_STYLE } from ".";
import { ROWS } from "../../mockData";
import ExploreLink from "../ExploreLink";
import { CheckedIcon } from "./components/CheckIcon";
import { AttributeValue } from "./types";

const getStringValue = (
  value: AttributeValue,
  isMobile: boolean
): JSX.Element => {
  return (
    (
      <Typography
        sx={{ textAlign: isMobile ? "end" : "center", width: "100%" }}
      >
        {value}
      </Typography>
    ) || <CheckedIcon checked={value as false} />
  );
};
const getIconValue = (value: AttributeValue): JSX.Element => {
  return <CheckedIcon checked={value as boolean} />;
};

export const getValueForAttribute = (
  features: IProductFeatureValue[],
  featureMasterID: number,
  isMobile: boolean
) => {
  const chosenFeature = features.find(
    (item) => item.featureMasterID === featureMasterID
  )?.productFeatureValue;

  if (chosenFeature) {
    if (chosenFeature === "true") {
      return getIconValue(true);
    }

    if (chosenFeature === "false") {
      return getIconValue(false);
    }

    return getStringValue(chosenFeature, isMobile);
  }

  return getIconValue(false);
};

export const getComponentByName = (
  theme: Theme,
  data: ICompareProduct,
  name: string
) => {
  switch (name) {
    case ROWS.img:
      return (
        <Box
          sx={{
            padding: "12px 16px",
            boxSizing: "border-box",
            width: "100%",
            height: { xs: 176, md: 156 },
            mx: "auto",
          }}
        >
          <CardMedia
            component="img"
            image={data.image}
            alt={data.name}
            width="100%"
            height="100%"
            sx={{ objectFit: "contain", bgcolor: "#F7F8FA" }}
          />
        </Box>
      );
    case ROWS.name:
      return (
        <Box
          sx={{
            padding: "4px 16px",
            boxSizing: "border-box",
          }}
        >
          <Typography
            sx={{
              fontWeight: "700",
              fontSize: "1.25rem !important",
              color: theme.palette.primary.main,
              textAlign: "center",
              height: "100%",
            }}
          >
            {data.name}
          </Typography>
        </Box>
      );
    case ROWS.button:
      return (
        <Box
          sx={{
            padding: "12px 16px 24px",
            boxSizing: "border-box",
          }}
        >
          <ExploreLink path={`${ROUTES.trackingDevices}/${data.id}`} />
        </Box>
      );
  }
};

export const getDeviceCellStyles = (
  theme: Theme,
  rowIndex: number,
  cellIndex: number,
  cellsLength: number
) => ({
  padding: "0px",
  border: "none",
  backgroundColor: theme.palette.common.white,

  minWidth: "235px",
  boxShadow: cellIndex === 0 ? "0 24px 48px -12px rgba(93,97,104,.1)" : "",

  borderLeft: BORDER_STYLE,
  borderTopLeftRadius: rowIndex == 0 && cellIndex === 0 ? "6px" : "0px",
  borderTopRightRadius:
    rowIndex == 0 && cellIndex === cellsLength - 1 ? "6px" : "0px",

  borderTop: rowIndex == 0 ? BORDER_STYLE : "none",
  borderRight: cellIndex === cellsLength - 1 ? BORDER_STYLE : "",
});

export const rowStyles = {
  ":nth-of-type(even)": {
    "& div": { backgroundColor: "background.default" },
  },
  ":nth-of-type(odd)": {
    "& div": { backgroundColor: "white" },
  },
};

export const getCharacteristicStyle = (
  rowIndex: number,
  rowsLength: number
) => ({
  display: "flex",
  alignItems: "center",
  padding: "8px 16px",
  height: "100%",
  boxSizing: "border-box",
  borderLeft: BORDER_STYLE,
  borderTop: BORDER_STYLE,
  borderTopLeftRadius: rowIndex === 0 ? "6px" : "0px",
  borderBottomLeftRadius: rowsLength - 1 === rowIndex ? "6px" : "0px",
  borderBottom: rowsLength - 1 === rowIndex ? BORDER_STYLE : "none",
});

export const getCharacteristicNameStyles = (theme: Theme) => ({
  width: "10px",
  height: "10px",
  boxSizing: "border-box",
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: "5px",
  marginRight: "12px",
});

export const getCharacteristicCellStyles = (
  rowIndex: number,
  cellIndex: number,
  rowsLength: number,
  cellsLength: number,
  isMobile: boolean
) => ({
  height: "78px",
  border: "none",
  padding: "0px",
  boxSizing: "border-box",
  borderLeft: BORDER_STYLE,
  borderBottom: rowsLength - 1 === rowIndex ? BORDER_STYLE : "none",
  ...(cellIndex === cellsLength - 1
    ? {
        borderRight: BORDER_STYLE,
        borderBottomRightRadius: rowsLength - 1 === rowIndex ? "6px" : "0px",
      }
    : null),
  ...(cellIndex === 0 && isMobile
    ? {
        borderBottomLeftRadius: rowsLength - 1 === rowIndex ? "6px" : "0px",
      }
    : null),
});

export const getCharacteristicValueStyle = (
  theme: Theme,
  rowIndex: number,
  cellIndex: number,
  rowsLength: number,
  cellsLength: number,
  isMobile: boolean
) => ({
  height: "100%",
  padding: "8px 16px",
  boxSizing: "border-box",
  color: theme.palette.grey[600],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...(cellsLength - 1 === cellIndex
    ? { borderBottomRightRadius: rowsLength - 1 === rowIndex ? "6px" : "0px" }
    : null),

  ...(cellIndex === 0 && isMobile
    ? { borderBottomLeftRadius: rowsLength - 1 === rowIndex ? "6px" : "0px" }
    : null),
});

export const getProductFeaturesOptions = (
  productFeatures: IProductFeatureConfig[]
) => {
  return productFeatures.map((feature) => ({
    ...feature,
    withStar:
      feature.featureMasterID === 1011 ||
      feature.featureMasterID === 1014 ||
      feature.featureMasterID === 1019 ||
      feature.featureMasterID === 1020,
  }));
};
